import type { SessionFeedbackAttributes, UserAttributes } from '@/server/models'

import { SESSION_FEEDBACK_TYPE_TUTOR, TutorSessionFeedbackRating } from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SeriesQ from './SeriesQ'
import SessionFeedbackFeaturedQ from './SessionFeedbackFeaturedQ'
import SessionHostQ from './SessionHostQ'
import SessionQ from './SessionQ'
import UserQ from './UserQ'

const SessionFeedbackQ = {
  ...makeQueryModelV2<SessionFeedbackAttributes>('session_feedback')([
    'id',
    'session_id',
    'user_id',
    'rating',
    'rating_tags',
    'comment',
    'nps',
    'internal_comment',
    'type',
    'created_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: SessionFeedbackQ.session_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SessionFeedbackQ.user_id,
    },
  }),

  getUser: (): SQLFragment<UserAttributes> =>
    SQL`${UserQ.where({
      id: SessionFeedbackQ.user_id,
    })}`,

  where: (
    {
      id,
      sessionId,
      userId,
      rating,
      type,
      include = [],
      limit = null,
      groupBy = null,
      order = null,
    }: {
      id?: number | null | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      rating?: TutorSessionFeedbackRating
      type?: string | SQLFragment<string> | null
      include?: IncludeAssociations
      limit?: number | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionFeedbackAttributes> => SQL`
        FROM ${SessionFeedbackQ}
        ${includeAssociationsToSql(SessionFeedbackQ, include)}
        WHERE
            ${type != null ? SQL`${SessionFeedbackQ.type} = ${type} AND` : SQL``}
            ${sessionId != null ? SQL`${SessionFeedbackQ.session_id} = ${sessionId} AND` : SQL``}
            ${userId != null ? SQL`${SessionFeedbackQ.user_id} = ${userId} AND` : SQL``}
            ${rating != null ? SQL`${SessionFeedbackQ.rating} = ${rating} AND` : SQL``}
            ${id != null ? SQL`${SessionFeedbackQ.id} = ${id} AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  isFeatured: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
        EXISTS(
            SELECT 1 ${SessionFeedbackFeaturedQ.where({
              sessionFeedbackId: SessionFeedbackQ.id,
              userId,
            })}
        )
    `,

  firstSessionFeedbackWithNotHelpful: ({ userId }: { userId: number }): SQLFragment<unknown> =>
    SQL`FROM ${SessionFeedbackQ} JOIN ${SessionHostQ} ON ${SessionHostQ.session_id} = ${SessionFeedbackQ.session_id}
          JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionHostQ.session_id}
          WHERE ${SessionHostQ.user_id} = ${userId}
          AND ${SessionFeedbackQ.type} = ${SESSION_FEEDBACK_TYPE_TUTOR}
          AND ${SessionFeedbackQ.rating} = ${TutorSessionFeedbackRating.NotHelpful}
        ORDER BY ${SessionQ.start_date} ASC
        LIMIT 1`,

  getFeedbackWithNPSInSeries: () => SQL`
    ${SessionFeedbackQ.where(
      { type: SESSION_FEEDBACK_TYPE_TUTOR },
      SQL`
        ${SessionFeedbackQ.nps} IS NOT NULL
        AND ${SessionFeedbackQ.session_id} IN (
          SELECT ${SessionQ.id} ${SessionQ.where({ seriesId: SeriesQ.id })}
        )`
    )}
  `,
}

export default SessionFeedbackQ
